import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { minMaxRangeRequiredZodObject } from "@@/utilities/set-within-range";
import { z } from "zod";
import {
    accommodationQuantityIdFactory,
    accommodationQuantityIdZodString,
} from "./accommodation-quantity-id";
import type {
    AccommodationQuantityType} from "./accommodation-quantity-types";
import {
    accommodationQuantityTypeZodEnum,
} from "./accommodation-quantity-types";

// The overridable parts of a quantity A provider
// could be able to set it's own min and max
// values for a a quantity type on a specific
// level, e.g. for a specific product
const accommodationQuantityZodObject = z.object({
    _id: accommodationQuantityIdZodString,
    _type: z.literal("ACCOMMODATION_QUANTITY"),
    _createdAt: isoAndUnixZodSchema,
    type: accommodationQuantityTypeZodEnum,
    minMax: minMaxRangeRequiredZodObject,
});

type AccommodationQuantity = z.infer<typeof accommodationQuantityZodObject>;
const _accommodationQuantityFactoryParamsZodObject =
    accommodationQuantityZodObject.omit({
        _id: true,
        _type: true,
        _createdAt: true,
    });
type FactoryParams = z.infer<
    typeof _accommodationQuantityFactoryParamsZodObject
>;

const accommodationQuantityFactory = (
    params: FactoryParams,
): AccommodationQuantity => {
    return {
        ...params,
        _id: accommodationQuantityIdFactory(params.type),
        _type: "ACCOMMODATION_QUANTITY",
        _createdAt: nowIsoAndUnix(),
    };
};

type hardCodedGlobalAccommodationQuantityMap = {
    [key in AccommodationQuantityType]: AccommodationQuantity;
};

const hardCodedGlobalAccommodationQuantity: hardCodedGlobalAccommodationQuantityMap =
    {
        ADULT: accommodationQuantityFactory({
            type: "ADULT",
            minMax: {
                min: 0,
                max: 150,
            },
        }),
        CHILD: accommodationQuantityFactory({
            type: "CHILD",
            minMax: {
                min: 0,
                max: 17,
            },
        }),
        INFANT: accommodationQuantityFactory({
            type: "INFANT",
            minMax: {
                min: 0,
                max: 2,
            },
        }),
        GUEST: accommodationQuantityFactory({
            type: "GUEST",
            minMax: {
                min: 0,
                max: 150,
            },
        }),
        ANIMAL: accommodationQuantityFactory({
            type: "ANIMAL",
            minMax: {
                min: 0,
                max: 150,
            },
        }),
    };

type AccommodationQuantities = {
    [key in AccommodationQuantityType]: number;
};
type AccommodationQuantityMap = Map<AccommodationQuantityType, number>;

const totallyEmptyAccommodationQuantities = () => ({
    ADULT: 0,
    CHILD: 0,
    INFANT: 0,
    GUEST: 0,
    ANIMAL: 0,
});

const getAccommodationQuantityGuest = (
    accommodationQuantities: AccommodationQuantities,
): number => {
    if (accommodationQuantities.ADULT)
        return accommodationQuantities.ADULT + accommodationQuantities.CHILD;
    return accommodationQuantities.GUEST;
};

export {
    accommodationQuantityFactory,
    accommodationQuantityZodObject,
    getAccommodationQuantityGuest,
    hardCodedGlobalAccommodationQuantity,
    totallyEmptyAccommodationQuantities,
};
export type {
    AccommodationQuantities,
    AccommodationQuantity,
    AccommodationQuantityMap,
};
